import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, BookOpen, ShieldAlert, Wrench, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function OEMDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/oem/manufacturers/${slug}`).then((r) => {
      setData(r.data);
      setActive(r.data.procedures?.[0] || null);
    });
  }, [slug]);

  if (!data) return <div className="text-slate-500">Loading...</div>;

  const { manufacturer, procedures } = data;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="oem-detail-page">
      <Link to="/oem" className="text-xs text-slate-400 hover:text-blue-400 flex items-center gap-1.5 uppercase tracking-wider font-bold">
        <ArrowLeft className="w-3.5 h-3.5" /> OEM Manufacturers
      </Link>

      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-white text-3xl" style={{ backgroundColor: manufacturer.color || "#1e293b" }}>
          {manufacturer.name.charAt(0)}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">OEM · {manufacturer.country}</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">{manufacturer.name}</h1>
          <div className="text-sm text-slate-400 mt-1">{procedures.length} documented procedures</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2">
          {procedures.map((p) => (
            <button
              key={p.id}
              data-testid={`procedure-${p.id}`}
              onClick={() => setActive(p)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${
                active?.id === p.id ? "bg-blue-600/15 border-blue-500/40" : "bg-[#0B1120] border-slate-800 hover:border-slate-700"
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[10px] uppercase tracking-wider">{p.type.replace("_", " ")}</Badge>
              </div>
              <div className="font-medium text-sm">{p.title}</div>
              <div className="text-xs text-slate-500 mt-1">{p.system}</div>
            </button>
          ))}
        </div>

        {active && (
          <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-400">{active.system}</div>
              <h2 className="font-display text-2xl font-medium mt-1">{active.title}</h2>
              <div className="text-sm text-slate-400 mt-2 leading-relaxed">{active.summary}</div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> OEM Document Excerpt</div>
              <div className="p-4 rounded-lg bg-slate-900/40 border-l-2 border-cyan-500/50 text-sm text-slate-300 italic leading-relaxed">
                "{active.doc_excerpt}"
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-2"><Wrench className="w-3.5 h-3.5" /> Tools Required</div>
                <ul className="text-sm text-slate-300 space-y-1">
                  {(active.tools || []).map((t, i) => <li key={i} className="flex items-start gap-2"><span className="text-blue-500">▸</span>{t}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Compliance</div>
                <div className="flex flex-wrap gap-1.5">
                  {(active.compliance || []).map((c) => <Badge key={c} className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{c}</Badge>)}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20 flex gap-3">
              <ShieldAlert className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-400 mb-1">Safety Risk if Skipped</div>
                <div className="text-sm text-slate-300 leading-relaxed">{active.risk}</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-800">
              <div className="text-xs text-slate-500">Estimated calibration fee</div>
              <div className="font-mono text-2xl text-emerald-400">${active.fee_estimate.toFixed(2)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
